// Consent-aware Meta Pixel (Facebook Pixel) loader.
//
// DSGVO/TTDSG compliance: marketing/advertising tracking requires explicit opt-in.
// Nothing loads or executes until the visitor grants Marketing-consent. When
// consent is revoked, we call fbq('consent','revoke') (Meta's official signal
// to stop tracking) and clear Meta's _fbp cookie so subsequent requests carry
// no identifier.

const PIXEL_ID = '1461046241681125';

let pixelLoaded = false;
let pixelLoadPromise = null;
let consentActive = false;

export function enableMetaPixel() {
  consentActive = true;
  if (pixelLoaded) {
    if (window.fbq) window.fbq('consent', 'grant');
    return Promise.resolve();
  }
  if (pixelLoadPromise) return pixelLoadPromise;

  pixelLoadPromise = new Promise((resolve, reject) => {
    // Inline-equivalent of Meta's official snippet, but only invoked AFTER consent.
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve();
      return;
    }

    if (!window.fbq) {
      const n = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      window.fbq = n;
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
    }

    // CRITICAL: queue consent + init + PageView BEFORE the script loads. The fbq
    // queue is replayed in order when fbevents.js executes, so any subsequent
    // track() calls (e.g. Lead on /guide) are guaranteed to land AFTER init.
    // Queueing them in script.onload would race with eager track() calls and
    // cause Meta to discard events fired before init.
    window.fbq('consent', 'grant');
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onload = () => { pixelLoaded = true; resolve(); };
    script.onerror = () => { pixelLoadPromise = null; reject(new Error('Meta Pixel script failed to load')); };

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });

  return pixelLoadPromise;
}

export function disableMetaPixel() {
  consentActive = false;
  if (typeof window !== 'undefined' && window.fbq) {
    try { window.fbq('consent', 'revoke'); } catch { /* noop */ }
  }
  clearMetaCookies();
}

// Fire a Meta Pixel standard or custom event. Silently no-ops if consent is not active.
// `eventName` is one of Meta's standard events ('Lead', 'Contact', 'CompleteRegistration', ...)
// or any custom event string (will be sent as 'trackCustom').
export function trackPixelEvent(eventName, params = {}, opts = {}) {
  if (!consentActive || typeof window === 'undefined' || !window.fbq) return;
  const STANDARD_EVENTS = new Set([
    'AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration', 'Contact',
    'CustomizeProduct', 'Donate', 'FindLocation', 'InitiateCheckout', 'Lead', 'PageView',
    'Purchase', 'Schedule', 'Search', 'StartTrial', 'SubmitApplication', 'Subscribe', 'ViewContent',
  ]);
  try {
    if (STANDARD_EVENTS.has(eventName)) {
      window.fbq('track', eventName, params, opts);
    } else {
      window.fbq('trackCustom', eventName, params, opts);
    }
  } catch (e) { /* noop */ }
}

export function isMetaPixelEnabled() { return consentActive && pixelLoaded; }

function clearMetaCookies() {
  if (typeof document === 'undefined') return;
  const names = ['_fbp', '_fbc', 'fr'];
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}

export const META_PIXEL_ID = PIXEL_ID;
